import { HeaderPage } from "@/components/HeaderPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table } from "@/components/Table";
import { CardContent } from "@/components/CardContent";
import {
  Search,
  Plus,
  Eye,
  Trash2,
  Power,
  PowerOff,
  GripVertical,
  Monitor,
  Smartphone,
} from "lucide-react";
import type { Banner } from "@/types/Banner";
import { MdOutlineEdit } from "react-icons/md";
import { Loading } from "@/components/Loading";
import {
  useGetBanners,
  useDeleteBanner,
  useToggleBannerActive,
} from "@/hooks/useBanners";
import { useState } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";

const typeConfig: Record<string, { label: string; icon: React.ReactNode }> = {
  DESKTOP: { label: "Desktop", icon: <Monitor className="h-4 w-4" /> },
  MOBILE: { label: "Mobile", icon: <Smartphone className="h-4 w-4" /> },
};

export const Banners = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [type, setType] = useState<string>("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState<string | null>(null);
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [bannerToToggle, setBannerToToggle] = useState<Banner | null>(null);

  const deleteMutation = useDeleteBanner();
  const toggleMutation = useToggleBannerActive();

  const { data, isLoading } = useGetBanners({
    page,
    limit: 10,
    type: type === "all" ? undefined : (type as "DESKTOP" | "MOBILE"),
    search: debouncedSearch || undefined,
  });

  const banners = data?.data || [];
  const total = data?.total || 0;

  const handleDeleteClick = (id: string) => {
    setBannerToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!bannerToDelete) return;

    try {
      await deleteMutation.mutateAsync(bannerToDelete);
      toast.success("Banner deletado com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["getBanners"] });
      setBannerToDelete(null);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao deletar banner");
    }
  };

  const handleToggleClick = (banner: Banner) => {
    setBannerToToggle(banner);
    setToggleDialogOpen(true);
  };

  const handleConfirmToggle = async () => {
    if (!bannerToToggle) return;

    try {
      await toggleMutation.mutateAsync(bannerToToggle.id);
      toast.success(
        bannerToToggle.isActive
          ? "Banner desativado com sucesso!"
          : "Banner ativado com sucesso!",
      );
      queryClient.invalidateQueries({ queryKey: ["getBanners"] });
      setBannerToToggle(null);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao alterar status do banner");
    }
  };

  return (
    <>
      <HeaderPage
        title="Banners"
        description="Gerencie os banners exibidos para os usuários do aplicativo"
      />

      <Card className="mb-6 mt-6 bg-white">
        <CardContentUI className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar banners..."
                className="pl-10 h-10 text-sm"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <div className="flex gap-3">
              <Select value={type} onValueChange={(v) => { setType(v); setPage(1); }}>
                <SelectTrigger className="w-45 h-10 text-sm">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Tipos</SelectItem>
                  <SelectItem value="DESKTOP">Desktop</SelectItem>
                  <SelectItem value="MOBILE">Mobile</SelectItem>
                </SelectContent>
              </Select>
              <Button
                className="h-10 text-sm px-4 text-white cursor-pointer bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))]"
                onClick={() => navigate("/banners/create")}
              >
                <Plus className="h-4 w-4 mr-2" />
                Novo Banner
              </Button>
            </div>
          </div>
        </CardContentUI>
      </Card>

      <CardContent
        title="Lista de Banners"
        content={
          isLoading ? (
            <Loading label="Carregando banners..." />
          ) : (
            <Table<Banner>
              columns={[
                {
                  key: "order",
                  label: "Ordem",
                  render: (item) => (
                    <div className="flex items-center gap-2">
                      <GripVertical className="h-4 w-4 text-gray-400" />
                      <span>{item.order}</span>
                    </div>
                  ),
                },
                {
                  key: "image",
                  label: "Imagem",
                  render: (item) => (
                    <div className="w-20 h-12 rounded overflow-hidden bg-gray-100">
                      <img
                        src={`${import.meta.env.VITE_BUCKET_URL}${item.key}`}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = "none";
                        }}
                      />
                    </div>
                  ),
                },
                { key: "title", label: "Título" },
                {
                  key: "description",
                  label: "Descrição",
                  render: (item) => (
                    <span className="line-clamp-2 max-w-xs text-sm text-muted-foreground">
                      {item.description || "—"}
                    </span>
                  ),
                },
                {
                  key: "type",
                  label: "Tipo",
                  render: (item) => {
                    const config = typeConfig[item.type];
                    return (
                      <div className="flex items-center gap-2">
                        {config?.icon}
                        <span>{config?.label || item.type}</span>
                      </div>
                    );
                  },
                },
                {
                  key: "isActive",
                  label: "Status",
                  render: (item) => (
                    <Badge variant={item.isActive ? "success" : "destructive"}>
                      {item.isActive ? "Ativo" : "Inativo"}
                    </Badge>
                  ),
                },
                {
                  key: "createdAt",
                  label: "Criado em",
                  render: (item) => formatDate(item.createdAt),
                },
                {
                  key: "actions",
                  label: "Ações",
                  render: (item) => (
                    <div className="flex items-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 cursor-pointer hover:bg-gray-100 transition-colors"
                        title="Visualizar"
                        onClick={() => navigate(`/banners/view/${item.id}`)}
                      >
                        <Eye className="h-5 w-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 cursor-pointer hover:bg-gray-100 transition-colors"
                        title="Editar"
                        onClick={() => navigate(`/banners/edit/${item.id}`)}
                      >
                        <MdOutlineEdit className="h-5 w-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 cursor-pointer hover:bg-gray-100 transition-colors"
                        title={item.isActive ? "Desativar" : "Ativar"}
                        onClick={() => handleToggleClick(item)}
                      >
                        {item.isActive ? (
                          <PowerOff className="h-5 w-5 text-orange-500" />
                        ) : (
                          <Power className="h-5 w-5 text-green-500" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 cursor-pointer hover:bg-gray-100 transition-colors"
                        title="Excluir"
                        onClick={() => handleDeleteClick(item.id)}
                      >
                        <Trash2 className="h-5 w-5 text-red-500" />
                      </Button>
                    </div>
                  ),
                },
              ]}
              data={banners}
              pagination={{
                total,
                page,
                limit: 10,
                totalPages: Math.ceil(total / 10),
              }}
              onPageChange={setPage}
            />
          )
        }
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Excluir Banner"
        description="Tem certeza que deseja excluir este banner? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        variant="destructive"
      />

      <ConfirmDialog
        open={toggleDialogOpen}
        onOpenChange={setToggleDialogOpen}
        title={bannerToToggle?.isActive ? "Desativar Banner" : "Ativar Banner"}
        description={
          bannerToToggle?.isActive
            ? "Tem certeza que deseja desativar este banner? Ele não será mais exibido aos usuários."
            : "Tem certeza que deseja ativar este banner? Ele será exibido aos usuários."
        }
        confirmText={bannerToToggle?.isActive ? "Desativar" : "Ativar"}
        cancelText="Cancelar"
        onConfirm={handleConfirmToggle}
        variant={bannerToToggle?.isActive ? "destructive" : "default"}
      />
    </>
  );
};
